/**
 * Real Videra demo workspaces — source of truth for sector content.
 * Used by V2Sectors hub and V2SectorPage detail view.
 */
export const sectorData = {
  'nhs': {
    slug: 'nhs',
    tag: 'NHS',
    workspaceName: 'NHS Estates Portfolio',
    badge: 'NHS Capital & Estates',
    category: 'Government · NHS Capital Programme Portfolio',
    title: 'A unified environment for NHS capital, estates and lifecycle.',
    intro: 'Aligned to NHS governance expectations. Supports business cases, capital delivery, estates assurance and lifecycle planning — with the evidence trail trusts and ICBs require.',
    demoDescription: 'Browse a real-shape multi-trust NHS portfolio with 16 Trusts, capital programme, lifecycle works, ERIC reporting and Tier-A/B/C compliance packs.',
    demoStats: ['16 Trusts', 'Capital programme', 'Lifecycle works', 'ERIC reporting', 'Tier-A/B/C compliance'],
    demoUrl: 'https://demo.videra.app/explore',
    painPoints: [
      'Business case evidence scattered across teams and PDFs',
      'Capital delivery without consistent stage-gate governance',
      'Estates assurance handled in spreadsheets',
      'Lifecycle planning disconnected from capital programmes',
      'ERIC and Tier-A/B/C reporting takes days, not minutes',
    ],
    howVidera: [
      'Multi-Trust portfolio view with capital programme rollup',
      'Stage-gated capital framework aligned to NHS governance',
      'Business case templates with auto-populated evidence',
      'Lifecycle works integrated into the capital programme',
      'ERIC reporting + Tier-A/B/C compliance packs auto-compiled',
    ],
    gradient: 'from-blue-600 via-blue-500 to-cyan-500',
    gradientSoft: 'from-blue-500/15 to-cyan-500/10',
    accentText: 'text-blue-200',
    accentBg: 'bg-blue-500/15 text-blue-100 border-blue-400/30',
    pillBg: 'bg-blue-500 text-white',
    badgePill: 'bg-blue-500/10 text-blue-700 border-blue-200',
    buttonGradient: 'from-cyan-500 to-blue-600',
  },

  'hard-services': {
    slug: 'hard-services',
    tag: 'VIDERA',
    workspaceName: 'Videra Healthcare',
    badge: 'Hard Services & FM',
    category: 'Facilities Management · Hard Services Portfolio',
    title: 'A governed delivery framework for PFI hard-services and FM.',
    intro: 'PFI hard-services on a single audit-ready framework — statutory compliance, planned maintenance, reactive workflows and lifecycle intelligence coordinated in one operating environment.',
    demoDescription: 'PFI hard-services on a single audit-ready framework: 4 sample healthcare sites, ~50 lifecycle and variation projects, gate documents, board packs and a live RAG rollup.',
    demoStats: ['4 healthcare sites', '~50 lifecycle / variation projects', 'Gate documents', 'Board packs', 'Live RAG rollup'],
    demoUrl: 'https://demo.videra.app/explore',
    painPoints: [
      'PFI obligations split across multiple disconnected systems',
      'Lifecycle and variation projects tracked in spreadsheets',
      'Gate documents incomplete or out-of-date',
      'Board packs assembled manually every cycle',
      'Reactive work orders with no governance or audit trail',
    ],
    howVidera: [
      'Audit-ready framework spanning every healthcare site',
      'Lifecycle and variation projects tracked through gate stages',
      'Gate documents auto-compiled from structured templates',
      'Board packs generated from live data in minutes',
      'Real-time RAG rollup across the entire portfolio',
    ],
    gradient: 'from-emerald-500 via-teal-500 to-cyan-500',
    gradientSoft: 'from-emerald-500/15 to-cyan-500/10',
    accentText: 'text-emerald-200',
    accentBg: 'bg-emerald-500/15 text-emerald-100 border-emerald-400/30',
    pillBg: 'bg-emerald-500 text-white',
    badgePill: 'bg-emerald-500/10 text-emerald-700 border-emerald-200',
    buttonGradient: 'from-emerald-500 to-teal-600',
  },

  'construction': {
    slug: 'construction',
    tag: 'C+',
    workspaceName: 'Construction+™',
    badge: 'Construction & Capital Projects',
    category: 'Construction · Tier-1 Main Contractor Portfolio',
    title: 'End-to-end delivery control from initiation to close-out.',
    intro: 'Vertically integrated for Tier-1 contractors and capital teams. Stage-gated delivery, design coordination, procurement, AI-assisted safety and bid support, with a 60-year Golden Thread spine.',
    demoDescription: 'Vertically integrated Tier-1 contractor portfolio: 50+ live projects across residential, commercial, healthcare and education at every stage from Initiation to Close-Out.',
    demoStats: ['50+ live projects', 'Residential · Commercial · Healthcare · Education', 'Stage-gated delivery', 'Design coordination · Procurement', 'AI safety & bid support', '60-year Golden Thread'],
    demoUrl: 'https://demo.videra.app/explore',
    painPoints: [
      'Pre-construction governance lost between teams',
      'Commercial variations without clear uplift logic',
      'Safety documentation incomplete or out-of-date',
      'Programme reporting prepared manually each month',
      'Handover data incomplete when assets go live',
    ],
    howVidera: [
      'Stage-gated delivery — Initiation to Close-Out',
      'Design coordination + procurement built into every gate',
      'AI-assisted safety classification and bid support',
      'Variation uplift logic and commercial assurance trails',
      '60-year Golden Thread — complete asset evidence spine',
    ],
    gradient: 'from-orange-500 via-red-500 to-rose-500',
    gradientSoft: 'from-orange-500/15 to-red-500/10',
    accentText: 'text-orange-200',
    accentBg: 'bg-orange-500/15 text-orange-100 border-orange-400/30',
    pillBg: 'bg-orange-500 text-white',
    badgePill: 'bg-orange-500/10 text-orange-700 border-orange-200',
    buttonGradient: 'from-orange-500 to-red-600',
  },
};

export const sectorList = ['nhs', 'hard-services', 'construction'].map((s) => sectorData[s]);
